import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TableSkeletonLoader } from '../../../components/SkeletonLoader/Table.SkeletonLoader';
import { useCallback, useMemo, useState } from 'react';
import { Button, styled, useTheme } from '@mui/material';
import { TableV1 } from '../../../components/Table/TableV1';
import { SearchInput } from '../../../components/SearchInput/SearchInput';
import { SelectItem } from '../../../types';
import { isSubmitter } from '../../../utils/roles';
import { Typography } from '../../../components/Typography/Typography';
import { useProxyAssignmentsTableColumns } from '../hooks/useProxyAssignmentsTableColumns';
import { useUsers } from '../../../queries/hooks/useUsers';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../../state/UIState';

const Wrapper = styled('div')``;

const FiltersSection = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ProxyAssignmentsTable = () => {
  const { isLoading: areUsersLoading, data: users } = useUsers();
  const { colors } = useTheme();
  const columns = useProxyAssignmentsTableColumns();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedOPs, setSelectedOPs] = useState<SelectItem[]>([]);
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const operatingPartners = useMemo(() => {
    if (!users) return [];
    return users.filter((user) => isSubmitter(user));
  }, [users]);

  const table = useReactTable({
    data: operatingPartners,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (user) => String(user.id),
  });

  const allOPs = useMemo(
    () => operatingPartners.sort().map((user) => ({ id: user.id, value: user.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operatingPartners]
  );

  const onChangeOPFilter = useCallback(
    (OPs: SelectItem[] | undefined) => {
      if (OPs) {
        setSelectedOPs(OPs);
        table.getColumn('name')?.setFilterValue(OPs);
      }
    },
    [setSelectedOPs, table]
  );

  const isResetDisabled = useMemo(() => {
    const selectedOPsFilter = columnFilters.find((filter) => filter.id === 'name')
      ?.value as SelectItem[];
    const searchFilter = columnFilters.find((filter) => filter.id === 'proxy')?.value as string;
    if (!searchFilter && (selectedOPsFilter ?? []).length === 0) return true;
    return false;
  }, [columnFilters]);

  const onFiltersReset = useCallback(() => {
    setSelectedOPs([]);
    table.getColumn('name')?.setFilterValue([]);
    table.getColumn('proxy')?.setFilterValue('');
  }, [table]);

  if (areUsersLoading || !users) return <TableSkeletonLoader />;

  return (
    <Wrapper>
      <FiltersSection>
        <SearchInput
          placeholder='Search'
          style={{ width: isSmallScreen ? '150px' : '370px', height: '36px' }}
          onClear={() => table.getColumn('proxy')?.setFilterValue('')}
          onChange={(e) => table.getColumn('proxy')?.setFilterValue(e.target.value)}
          value={table.getColumn('proxy')?.getFilterValue() ?? ''}
        />
        <Multiselect
          style={{ width: isSmallScreen ? '150px' : '200px', marginRight: '14px' }}
          fieldPlaceholder='Select submitter'
          options={allOPs}
          value={selectedOPs}
          onChange={(_, OPs) => onChangeOPFilter(OPs)}
        />
        <Button
          onClick={onFiltersReset}
          variant='text'
          style={{ height: '28px', marginTop: '2px' }}
          disabled={isResetDisabled}
          sx={{
            '.MuiButton-startIcon': { marginRight: '2px' },
            '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
          }}
        >
          <Typography variant='subtitle1' color={colors.textAccent.default}>
            Reset Filters
          </Typography>
        </Button>
      </FiltersSection>
      <TableV1
        table={table}
        height='auto'
        wrapperStyle={{
          maxHeight: 'calc(100vh - 288px)',
          overflowX: 'scroll',
          border: `1px solid ${colors.border.default}`,
        }}
      />
    </Wrapper>
  );
};
